import React, { useState, useRef, useCallback } from "react";
import DocumentService, {
  CreateDocumentData,
} from "../../../../services/documentService";
import { Patient } from "../../PatientCard";

interface DocumentUploadModalProps {
  patient: Patient;
  isOpen: boolean;
  onClose: () => void;
  onDocumentUploaded?: (documentId: number) => void;
}

const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  patient,
  isOpen,
  onClose,
  onDocumentUploaded,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] =
    useState<CreateDocumentData["document_type"]>("other");
  const [subcategory, setSubcategory] = useState("");
  const [isConfidential, setIsConfidential] = useState(false);
  const [accessLevel, setAccessLevel] =
    useState<CreateDocumentData["access_level"]>("medical_staff");
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [notes, setNotes] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {}
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dragActive, setDragActive] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const categories = [
    { value: "lab_result", label: "Lab Result" },
    { value: "imaging", label: "Imaging" },
    { value: "dicom", label: "DICOM/Medical Images" },
    { value: "xray_image", label: "X-Ray Image" },
    { value: "prescription", label: "Prescription" },
    { value: "medical_report", label: "Medical Report" },
    { value: "consent_form", label: "Consent Form" },
    { value: "insurance_document", label: "Insurance" },
    { value: "referral_letter", label: "Referral" },
    { value: "medical_certificate", label: "Medical Certificate" },
    { value: "discharge_summary", label: "Discharge Summary" },
    { value: "other", label: "Other" },
  ];

  const accessLevels = [
    { value: "public", label: "Public" },
    { value: "medical_staff", label: "Medical Staff" },
    { value: "doctor_only", label: "Doctor Only" },
    { value: "admin_only", label: "Admin Only" },
  ];

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      setSelectedFiles([]);
      setTitle("");
      setDescription("");
      setCategory("other");
      setSubcategory("");
      setIsConfidential(false);
      setAccessLevel("medical_staff");
      setTags([]);
      setTagInput("");
      setNotes("");
      setErrors({});
      setUploadProgress({});
    }
  }, [isOpen]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      handleFileSelection(files);
      e.dataTransfer.clearData();
    }
  }, []);

  const handleFileSelection = (files: File[]) => {
    const validFiles: File[] = [];
    const fileErrors: Record<string, string> = {};

    files.forEach((file, index) => {
      const validation = DocumentService.validateFile(file);
      if (validation.isValid) {
        validFiles.push(file);
      } else {
        fileErrors[`file_${index}`] = validation.error || "Invalid file";
      }
    });

    if (Object.keys(fileErrors).length > 0) {
      setErrors(fileErrors);
    } else {
      setErrors({});
    }

    setSelectedFiles((prev) => [...prev, ...validFiles]);

    if (validFiles.length === 1 && !title) {
      setTitle(validFiles[0].name.replace(/\.[^/.]+$/, ""));
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      handleFileSelection(files);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const addTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags((prev) => [...prev, tagInput.trim()]);
      setTagInput("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags((prev) => prev.filter((tag) => tag !== tagToRemove));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (selectedFiles.length === 0) {
      newErrors.files = "Please select at least one file";
    }

    if (!title.trim()) {
      newErrors.title = "Title is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpload = async () => {
    if (!validateForm()) return;

    setIsUploading(true);
    const uploadedDocuments: number[] = [];

    try {
      const uploadPromises = selectedFiles.map(async (file, i) => {
        setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));

        const documentData: CreateDocumentData = {
          patient_id: patient.id,
          title: selectedFiles.length === 1 ? title : `${title} (${i + 1})`,
          description: description || undefined,
          document_type: category,
          document_category: subcategory || undefined,
          subcategory: subcategory || undefined,
          is_confidential: isConfidential,
          access_level: accessLevel,
          tags: tags.length > 0 ? tags : undefined,
          notes: notes || undefined,
          file,
        };

        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => {
            const currentProgress = prev[file.name] || 0;
            if (currentProgress < 90) {
              return { ...prev, [file.name]: currentProgress + 10 };
            }
            return prev;
          });
        }, 100);

        try {
          const document = await DocumentService.uploadDocument(documentData);
          clearInterval(progressInterval);
          setUploadProgress((prev) => ({ ...prev, [file.name]: 100 }));
          return document.id!;
        } catch (error) {
          clearInterval(progressInterval);
          throw error;
        }
      });

      const results = await Promise.all(uploadPromises);
      uploadedDocuments.push(...results);

      if (onDocumentUploaded) {
        uploadedDocuments.forEach((id) => onDocumentUploaded(id));
      }

      onClose();
    } catch (error: any) {
      setErrors({ submit: error.message || "Failed to upload documents" });
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    zIndex: 1000,
    display: "flex",
    alignItems: isMobile ? "flex-start" : "center",
    justifyContent: "center",
    padding: isMobile ? "10px" : "20px",
    overflow: "auto",
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: "#ffffff",
    borderRadius: isMobile ? "12px" : "16px",
    width: "100%",
    maxWidth: isMobile ? "100%" : "800px",
    maxHeight: isMobile ? "100%" : "90vh",
    overflow: "hidden",
    boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
    display: "flex",
    flexDirection: "column",
    margin: isMobile ? "10px 0" : "auto",
  };

  const headerStyle: React.CSSProperties = {
    padding: isMobile ? "16px" : isTablet ? "18px" : "20px",
    borderBottom: "1px solid #e5e7eb",
    backgroundColor: "#f8fafc",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: isMobile ? "12px" : isTablet ? "14px" : "16px",
    flexShrink: 0,
  };

  const contentStyle: React.CSSProperties = {
    flex: 1,
    overflow: "auto",
    padding: isMobile ? "16px" : "24px",
  };
  const buttonStyle = (
    variant: "primary" | "secondary" | "danger" = "primary"
  ): React.CSSProperties => {
    const variants = {
      primary: { background: "#f59e0b", color: "white" },
      secondary: { background: "#e5e7eb", color: "#374151" },
      danger: { background: "#ef4444", color: "white" },
    };

    return {
      padding: isMobile ? "8px 16px" : "10px 20px",
      borderRadius: "8px",
      border: "none",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s ease",
      ...variants[variant],
      textAlign: "center",
    };
  };
  const footerStyle: React.CSSProperties = {
    padding: "20px",
    borderTop: "1px solid #e5e7eb",
    backgroundColor: "#f8fafc",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: isMobile ? "column" : "row",
    gap: isMobile ? "16px" : "0",
    flexShrink: 0,
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: isMobile ? "10px 14px" : "12px 16px",
    border: "2px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: isMobile ? "13px" : "14px",
    outline: "none",
    transition: "border-color 0.2s ease",
  };

  const dropzoneStyle: React.CSSProperties = {
    border: `2px dashed ${dragActive ? "#f59e0b" : "#e5e7eb"}`,
    borderRadius: "8px",
    padding: isMobile ? "24px" : "40px",
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: dragActive ? "#fffbeb" : "#fafafa",
    marginBottom: isMobile ? "16px" : "24px",
  };

  return (
    <div
      style={overlayStyle}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={modalStyle}>
        {/* Header */}
        <div style={headerStyle} className="nn">
          <div style={{ flex: 1 }}>
            <h2
              style={{
                fontSize: isMobile ? "18px" : isTablet ? "19px" : "20px",
                fontWeight: "600",
                margin: "0 0 4px 0",
                color: "#1f2937",
              }}
            >
              📄 Upload Documents
            </h2>
            <p
              style={{
                fontSize: isMobile ? "13px" : isTablet ? "13px" : "14px",
                color: "#64748b",
                margin: 0,
              }}
            >
              Upload documents for{" "}
              {patient.full_name ||
                `${patient.first_name} ${patient.last_name}`}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ ...buttonStyle("secondary"), padding: "6px 10px" }}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={contentStyle}>
          {/* Patient Info */}
          <div
            style={{
              background: "#fffbeb",
              border: "1px solid #fde68a",
              borderRadius: "8px",
              padding: "16px",
              marginBottom: "24px",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : isTablet
                  ? "repeat(2, 1fr)"
                  : "repeat(3, 1fr)",
                gap: "12px",
              }}
            >
              <div>
                <strong>Patient:</strong>{" "}
                {patient.full_name ||
                  `${patient.first_name} ${patient.last_name}`}
              </div>
              <div>
                <strong>Age:</strong> {patient.age} years
              </div>
              <div>
                <strong>Phone:</strong> {patient.phone}
              </div>
            </div>
          </div>

          {/* File Upload Area */}
          <div
            style={dropzoneStyle}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div
              style={{
                fontSize: isMobile ? "32px" : "48px",
                marginBottom: "12px",
              }}
            >
              📁
            </div>
            <h3
              style={{
                marginBottom: "8px",
                color: "#374151",
                fontSize: isMobile ? "14px" : "16px",
              }}
            >
              Drag & drop files here, or click to select
            </h3>
            <p
              style={{
                color: "#6b7280",
                marginBottom: "16px",
                fontSize: isMobile ? "11px" : "14px",
                lineHeight: "1.4",
              }}
            >
              Supports: PDF, Images, Word, Excel, Text files, DICOM files (Max
              50MB each)
            </p>
            <button
              style={{
                ...buttonStyle("primary"),
                pointerEvents: "none",
                fontSize: isMobile ? "12px" : "14px",
              }}
            >
              Choose Files
            </button>
            <input
              ref={fileInputRef}
              type="file"
              id="file_upload"
              name="file_upload"
              multiple
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.xls,.xlsx,.txt,.csv,.dcm"
              onChange={handleFileInputChange}
              style={{ display: "none" }}
            />
          </div>

          {errors.files && (
            <div
              style={{
                color: "#ef4444",
                fontSize: isMobile ? "12px" : "14px",
                marginBottom: "16px",
                padding: "12px",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "6px",
              }}
            >
              {errors.files}
            </div>
          )}

          {/* Selected Files */}
          {selectedFiles.length > 0 && (
            <div
              style={{
                marginBottom: isMobile ? "16px" : "24px",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                padding: isMobile ? "12px" : "16px",
              }}
            >
              <h4
                style={{
                  fontSize: isMobile ? "14px" : "16px",
                  fontWeight: "600",
                  marginBottom: "12px",
                }}
              >
                Selected Files ({selectedFiles.length})
              </h4>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: isMobile ? "6px 10px" : "8px 12px",
                      background: "#f9fafb",
                      border: "1px solid #f3f4f6",
                      borderRadius: "6px",
                      flexWrap: isMobile ? "wrap" : "nowrap",
                      gap: isMobile ? "8px" : "0",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <span style={{ fontSize: isMobile ? "14px" : "16px" }}>
                        {DocumentService.getFileTypeIcon(file.type)}
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: isMobile ? "12px" : "14px",
                            fontWeight: "500",
                            wordBreak: "break-word",
                          }}
                        >
                          {file.name}
                        </div>
                        <div
                          style={{
                            fontSize: isMobile ? "10px" : "12px",
                            color: "#6b7280",
                          }}
                        >
                          {DocumentService.formatFileSize(file.size)}
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexShrink: 0,
                      }}
                    >
                      {uploadProgress[file.name] !== undefined && (
                        <div style={{ width: isMobile ? "80px" : "100px" }}>
                          <div
                            style={{
                              width: "100%",
                              height: "4px",
                              background: "#e5e7eb",
                              borderRadius: "2px",
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                width: `${uploadProgress[file.name]}%`,
                                height: "100%",
                                background: "#f59e0b",
                                transition: "width 0.3s ease",
                              }}
                            />
                          </div>
                          <div
                            style={{
                              fontSize: "9px",
                              textAlign: "center",
                              marginTop: "2px",
                            }}
                          >
                            {uploadProgress[file.name]}%
                          </div>
                        </div>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(index);
                        }}
                        style={{
                          ...buttonStyle("danger"),
                          padding: isMobile ? "3px 6px" : "4px 8px",
                          fontSize: isMobile ? "10px" : "12px",
                        }}
                        disabled={isUploading}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Document Metadata */}
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              padding: isMobile ? "16px" : "20px",
              marginBottom: isMobile ? "16px" : "24px",
            }}
          >
            <h4
              style={{
                fontSize: isMobile ? "14px" : "16px",
                fontWeight: "600",
                marginBottom: "16px",
              }}
            >
              Document Information
            </h4>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr",
                gap: "20px",
                marginBottom: "20px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: isMobile ? "13px" : "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  Title *
                </label>
                <input
                  type="text"
                  id="document_title"
                  name="document_title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Document title"
                  style={{
                    ...inputStyle,
                    borderColor: errors.title ? "#ef4444" : "#e5e7eb",
                  }}
                />

                {errors.title && (
                  <div
                    style={{
                      color: "#ef4444",
                      fontSize: isMobile ? "11px" : "12px",
                      marginTop: "4px",
                    }}
                  >
                    {errors.title}
                  </div>
                )}
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: isMobile ? "13px" : "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  Category
                </label>
                <select
                  value={category}
                  onChange={(e) =>
                    setCategory(
                      e.target.value as CreateDocumentData["document_type"]
                    )
                  }
                  style={inputStyle}
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: "20px",
                marginBottom: "20px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: isMobile ? "13px" : "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  Subcategory
                </label>
                <input
                  type="text"
                  id="subcategory"
                  name="subcategory"
                  value={subcategory}
                  onChange={(e) => setSubcategory(e.target.value)}
                  placeholder="e.g., Blood test, X-ray, etc."
                  style={inputStyle}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: isMobile ? "13px" : "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  Access Level
                </label>
                <select
                  value={accessLevel}
                  onChange={(e) =>
                    setAccessLevel(
                      e.target.value as CreateDocumentData["access_level"]
                    )
                  }
                  style={inputStyle}
                >
                  {accessLevels.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: isMobile ? "13px" : "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of the document..."
                rows={isMobile ? 2 : 3}
                style={{ ...inputStyle, resize: "none" }}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: isMobile ? "13px" : "14px",
                }}
              >
                <input
                  type="checkbox"
                  id="is_confidential"
                  name="is_confidential"
                  checked={isConfidential}
                  onChange={(e) => setIsConfidential(e.target.checked)}
                />

                <span style={{ fontWeight: "500" }}>Mark as Confidential</span>
              </label>
              <p
                style={{
                  fontSize: isMobile ? "11px" : "12px",
                  color: "#6b7280",
                  marginTop: "4px",
                  lineHeight: "1.4",
                }}
              >
                Confidential documents require special permissions to view
              </p>
            </div>

            {/* Tags */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: isMobile ? "13px" : "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                Tags
              </label>
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  marginBottom: "8px",
                  flexWrap: isMobile ? "wrap" : "nowrap",
                }}
              >
                <input
                  type="text"
                  id="tag_input"
                  name="tag_input"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add a tag"
                  style={{
                    ...inputStyle,
                    flex: 1,
                    minWidth: isMobile ? "120px" : "auto",
                  }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                />

                <button
                  onClick={addTag}
                  style={{
                    ...buttonStyle("secondary"),
                    flexShrink: 0,
                  }}
                  type="button"
                >
                  Add
                </button>
              </div>
              {tags.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: isMobile ? "3px 6px" : "4px 8px",
                        background: "#f59e0b",
                        color: "white",
                        borderRadius: "12px",
                        fontSize: isMobile ? "11px" : "12px",
                        fontWeight: "500",
                      }}
                    >
                      {tag}
                      <button
                        onClick={() => removeTag(tag)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "white",
                          cursor: "pointer",
                          padding: "0",
                          fontSize: isMobile ? "9px" : "10px",
                        }}
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: isMobile ? "13px" : "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes about this document..."
                rows={2}
                style={{ ...inputStyle, resize: "none" }}
              />
            </div>
          </div>

          {errors.submit && (
            <div
              style={{
                color: "#ef4444",
                fontSize: isMobile ? "12px" : "14px",
                marginBottom: "16px",
                padding: "12px",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "6px",
              }}
            >
              {errors.submit}
            </div>
          )}

          {/* Footer */}
          <div style={footerStyle}>
            <div
              style={{
                fontSize: isMobile ? "13px" : "13px",
                color: "#6b7280",
                textAlign: "center",
              }}
            >
              {selectedFiles.length > 0 && (
                <>
                  {selectedFiles.length} file(s) selected • Total size:{" "}
                  {DocumentService.formatFileSize(
                    selectedFiles.reduce((total, file) => total + file.size, 0)
                  )}
                </>
              )}
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                width: isMobile ? "100%" : "auto",
              }}
            >
              <button
                onClick={onClose}
                style={{
                  ...buttonStyle("secondary"),
                  flex: isMobile ? "1" : "none",
                }}
                disabled={isUploading}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                style={{
                  ...buttonStyle("primary"),
                  opacity: isUploading ? 0.7 : 1,
                  cursor: isUploading ? "not-allowed" : "pointer",
                  flex: isMobile ? "1" : "none",
                }}
                disabled={isUploading || selectedFiles.length === 0}
              >
                {isUploading ? "⏳ Uploading..." : "📄 Upload Documents"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          input:focus, select:focus, textarea:focus {
            border-color: #f59e0b !important;
            box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1) !important;
          }
          
          button:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }
          
          @media (max-width: 768px) {
            .mobile-stack {
              flex-direction: column !important;
            }
            
            .mobile-full-width {
              width: 100% !important;
            }
            
            .mobile-grid-single {
              grid-template-columns: 1fr !important;
            }
            
            .mobile-text-left {
              text-align: left !important;
            }
            
            button:hover:not(:disabled) {
              transform: none;
              box-shadow: none;
            }
          }
          
          @media (max-width: 480px) {
            .mobile-sm-stack {
              flex-direction: column !important;
            }
            
            .mobile-sm-center {
              justify-content: center !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default DocumentUploadModal;
